import type { OverrideConfig } from "src/components/destinations/types";
import { hasUnlockedFields } from "src/components/destinations/utils";

export type GetSyncFormVisibilityArgs = {
  syncTemplateId: string | number | null | undefined;
  /** Build with `getOverrideConfigIncludingEmptyFields`, so empty template fields count as unlocked. */
  overrideConfig: OverrideConfig | null | undefined;
  templateRequiresCompletion: boolean | undefined;
};

/**
 * Whether a sync created from a template has anything for the user to configure.
 * True when the template requires completion or has unlocked fields.
 */
export function templateHasSyncEditableFields({
  overrideConfig,
  templateRequiresCompletion,
}: Omit<GetSyncFormVisibilityArgs, "syncTemplateId">): boolean {
  return (
    Boolean(templateRequiresCompletion) || hasUnlockedFields(overrideConfig)
  );
}

/**
 * Single source of truth for whether the sync config form should render.
 *
 * The form renders when:
 * - No sync template is being used, OR
 * - The template requires completion (has empty required fields), OR
 * - The template has unlocked fields
 */
export function getSyncFormVisibility({
  syncTemplateId,
  overrideConfig,
  templateRequiresCompletion,
}: GetSyncFormVisibilityArgs) {
  const usesSyncTemplate = Boolean(syncTemplateId);

  const shouldShowForm =
    !usesSyncTemplate ||
    templateHasSyncEditableFields({
      overrideConfig,
      templateRequiresCompletion,
    });
  return { usesSyncTemplate, shouldShowForm };
}
